import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { paymentsAPI } from "@/lib/api";
import { Check, Zap, Crown, Star } from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "0",
    period: "",
    icon: Star,
    features: ["1 supporting statement/month", "2 application analyses/month", "1 interview simulation/month", "Basic feedback"],
    notIncluded: ["Band coaching", "CV rewriting", "Word export"],
  },
  {
    id: "pro",
    name: "Pro",
    price: "19",
    period: "/month",
    icon: Zap,
    features: [
      "10 Tailored NHS Supporting Information (SI) Generations",
      "JD & Person Specification Alignment",
      "Missing Skills Analysis",
      "Professional Structured Output",
      "Limited Band Coaching (5 sessions per billing cycle)",
      "CV Rewriter Access (5 per billing cycle)",
      "1 Interview Simulation per billing cycle",
      "Secure Document Processing",
    ],
    notIncluded: [],
    popular: true,
  },
  {
    id: "premium",
    name: "Premium",
    price: "39",
    period: "/month",
    icon: Crown,
    features: [
      "Unlimited NHS Supporting Information (SI) Generations",
      "Unlimited Band-Calibrated Interview Simulations",
      "10 Detailed NHS-Standard Interview Questions per Simulation",
      "Structured Panel-Level Feedback on Every Response",
      "Clinical & Non-Clinical Evaluation Logic",
      "Leadership & Governance Calibration by Band",
      "JD & Person Specification Specific Question Mapping",
      "Secure Document Processing",
    ],
    notIncluded: [],
  },
];

export default function PricingPage() {
  const { user } = useAuth();
  const currentPlan = user?.subscription_plan || "free";

  const handleUpgrade = async (planId) => {
    if (planId === "free" || planId === currentPlan) return;
    try {
      const originUrl = window.location.origin;
      const res = await paymentsAPI.checkout({ plan: planId, origin_url: originUrl });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Checkout failed";
      if (errorMsg === "Not authenticated" || err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        // Redirect to login after short delay
        setTimeout(() => window.location.href = "/login", 1500);
      } else {
        toast.error(errorMsg);
      }
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-5xl mx-auto" data-testid="pricing-page">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>Subscription Plans</h1>
          <p className="text-slate-500 mt-2">Choose the plan that fits your career goals.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan) => {
            const isCurrent = currentPlan === plan.id;
            return (
              <Card
                key={plan.id}
                className={`relative rounded-xl transition-all ${plan.popular ? "border-2 border-[#003087] shadow-lg" : "border border-slate-200 shadow-sm"}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#003087] text-white px-3">Most Popular</Badge>
                  </div>
                )}
                <CardContent className="p-6 pt-8">
                  <div className="flex items-center gap-2 mb-4">
                    <plan.icon className="w-5 h-5 text-[#003087]" />
                    <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Public Sans" }}>{plan.name}</h3>
                    {isCurrent && <Badge className="bg-[#007F3B]/10 text-[#007F3B] ml-auto">Current</Badge>}
                  </div>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-bold text-[#003087]">&pound;{plan.price}</span>
                    <span className="text-slate-500">{plan.period}</span>
                  </div>
                  <ul className="space-y-2.5 mb-6">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-[#007F3B] mt-0.5 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <Button className="w-full bg-slate-100 text-slate-500 cursor-default" disabled data-testid={`plan-btn-${plan.id}`}>Current Plan</Button>
                  ) : plan.id === "free" ? (
                    <Button className="w-full bg-slate-100 text-slate-500 cursor-default" disabled data-testid={`plan-btn-${plan.id}`}>Free Tier</Button>
                  ) : (
                    <Button
                      className={`w-full ${plan.popular ? "bg-[#003087] text-white hover:bg-[#002366]" : "bg-white text-[#003087] border border-[#003087] hover:bg-[#F0F4F8]"}`}
                      onClick={() => handleUpgrade(plan.id)}
                      data-testid={`plan-btn-${plan.id}`}
                    >
                      Upgrade to {plan.name}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
